import { keyframes } from '@emotion/react';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { IconButton, Popover } from '@mui/material';
import React from 'react';
import { BLUE, RING_TRACK } from '../studentStyles';
import MediaErrorNotice from './MediaErrorNotice';
import useMockMediaFailure from './useMockMediaFailure';

const audioPulse = keyframes`
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.15); opacity: 0.7; }
    100% { transform: scale(1); opacity: 1; }
`;

// A soft ring that draws the eye to audio the student still has to hear.
const needsListenRing = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(47, 111, 237, 0.45); }
    70% { box-shadow: 0 0 0 10px rgba(47, 111, 237, 0); }
    100% { box-shadow: 0 0 0 0 rgba(47, 111, 237, 0); }
`;

interface AudioPlayButtonProps {
    ariaLabel: string;
    size?: number;
    durationMs?: number;
    disabled?: boolean;
    // Required listening: pulse until the clip has been heard once.
    required?: boolean;
    // Fires when the (fake) clip finishes playing — listening requirements key off this.
    onPlayed?: () => void;
    onPlayingChange?: (playing: boolean) => void;
}

// Fake audio playback: shows a transient "playing" pulse instead of real sound.
export default function AudioPlayButton({ ariaLabel, size = 44, durationMs = 1600, disabled = false, required = false, onPlayed, onPlayingChange }: AudioPlayButtonProps) {
    const [playing, setPlaying] = React.useState(false);
    const [heard, setHeard] = React.useState(false);
    const timeoutRef = React.useRef<number>();
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const takeFailure = useMockMediaFailure();
    const [audioError, setAudioError] = React.useState(false);
    const onPlayedRef = React.useRef(onPlayed);
    onPlayedRef.current = onPlayed;
    const onPlayingChangeRef = React.useRef(onPlayingChange);
    onPlayingChangeRef.current = onPlayingChange;

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const handleClick = () => {
        window.clearTimeout(timeoutRef.current);
        if (takeFailure('audio')) { setAudioError(true); return; }
        setAudioError(false);
        setPlaying(true);
        onPlayingChangeRef.current?.(true);
        timeoutRef.current = window.setTimeout(() => {
            setPlaying(false);
            onPlayingChangeRef.current?.(false);
            setHeard(true);
            onPlayedRef.current?.();
        }, durationMs);
    };

    const iconSize = Math.round(size * 0.55);
    return (
        <>
        <IconButton
            ref={buttonRef}
            aria-label={ariaLabel}
            aria-pressed={playing}
            onClick={handleClick}
            disabled={disabled}
            sx={{
                '@media (prefers-reduced-motion: reduce)': { '&, & *': { animation: 'none !important' } },
                width: Math.max(44, size),
                height: Math.max(44, size),
                color: BLUE,
                border: `1px solid ${required && !heard ? BLUE : RING_TRACK}`,
                backgroundColor: playing ? `${BLUE}14` : 'transparent',
                flexShrink: 0,
                animation: required && !heard && !playing && !disabled ? `${needsListenRing} 1.6s ease-out infinite` : undefined,
            }}
        >
            {playing
                ? <VolumeUpRoundedIcon sx={{ fontSize: iconSize, animation: `${audioPulse} 900ms ease-in-out infinite` }} />
                : <PlayArrowRoundedIcon sx={{ fontSize: iconSize }} />}
        </IconButton>
        <Popover open={audioError} anchorEl={buttonRef.current} onClose={() => setAudioError(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{ sx: { width: 290, maxWidth: 'calc(100vw - 32px)', borderRadius: '14px' } }}>
            <MediaErrorNotice failure="audio" onRetry={handleClick} />
        </Popover>
        </>
    );
}
